import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import ICustomer from '../../_interfaces/ICustomer';
import ISite from '../../_interfaces/ISite';
import { AuthenticationService } from '../../_services/authentication/authentication.service';
import { SessionService } from '../../_services/session/session.service';

@Component({
  standalone: true,
  selector: 'app-entry',
  templateUrl: './entry.component.html',
  styleUrls: ['./entry.component.scss'],
  imports: [CommonModule, TranslatePipe],
})
export class EntryComponent implements OnInit {
  public loading = true;
  public error = false;
  public modeSelectionRequired = false;
  public switchingMode = false;
  public availableModes: Array<'professional' | 'patient'> = [];
  public selectedMode: 'professional' | 'patient' = 'professional';
  private cachedContext: any = null;
  private modeResolvedByUser = false;

  constructor(
    private authService: AuthenticationService,
    private session: SessionService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.resolveEntry();
  }

  public retry() {
    this.resolveEntry();
  }

  public chooseMode(mode: 'professional' | 'patient') {
    if (this.switchingMode) return;

    this.modeResolvedByUser = true;
    this.switchingMode = true;
    this.authService.switchMode(mode).subscribe({
      next: (context: any) => {
        this.switchingMode = false;
        this.modeSelectionRequired = false;
        this.handleContext(context);
      },
      error: () => {
        this.switchingMode = false;
        this.error = true;
      },
    });
  }

  private resolveEntry() {
    this.loading = true;
    this.error = false;
    this.modeSelectionRequired = false;
    this.modeResolvedByUser = false;

    this.authService.loadContext().subscribe({
      next: (context) => {
        this.handleContext(context);
      },
      error: () => {
        this.loading = false;
        this.error = true;
      },
    });
  }

  private handleContext(context: any) {
    this.cachedContext = context;
    const user = context?.user;

    if (!user) {
      this.authService.closeSession();
      this.loading = false;
      this.router.navigate(['']);
      return;
    }

    if (user.superuser && !context?.session?.is_acting) {
      this.loading = false;
      this.router.navigate(['/administration/customers']);
      return;
    }

    const activeMode =
      context?.session?.active_mode === 'patient' ? 'patient' : 'professional';
    const availableModes = this.normalizeModes(context?.availableModes);
    this.availableModes = availableModes;
    this.selectedMode = activeMode;

    if (user?.superuser && activeMode !== 'professional') {
      this.authService.switchMode('professional').subscribe({
        next: (updatedContext: any) => this.handleContext(updatedContext),
        error: () => {
          this.loading = false;
          this.error = true;
        },
      });
      return;
    }

    if (activeMode === 'patient') {
      this.loading = false;
      this.router.navigate(['/patient/home']);
      return;
    }

    if (context?.noClinics) {
      this.loading = false;
      this.router.navigate(['/context/no-clinics']);
      return;
    }

    const activeCustomer = this.getActiveCustomer(context);
    if (!activeCustomer) {
      this.loading = false;
      this.router.navigate(['/context/no-clinics']);
      return;
    }

    const sites = activeCustomer.sites || [];
    const activeSite = context?.activeSite;

    if (!activeSite) {
      const preferredLandingSiteId = this.resolvePreferredLandingSiteId(
        context,
        activeCustomer,
        sites,
      );
      if (preferredLandingSiteId) {
        this.switchClinicAndContinue(preferredLandingSiteId);
        return;
      }

      if (sites.length === 1 && sites[0]?.id) {
        this.switchClinicAndContinue(+sites[0].id);
        return;
      }

      this.loading = false;
      this.router.navigate(['/context/select-clinic']);
      return;
    }

    this.loading = false;
    this.navigateToOperationalRoute(context, activeCustomer, activeSite);
  }

  private normalizeModes(
    availableModes: any,
  ): Array<'professional' | 'patient'> {
    const values = Array.isArray(availableModes) ? availableModes : [];
    return values.filter(
      (mode: string) => mode === 'professional' || mode === 'patient',
    );
  }

  private resolvePreferredLandingSiteId(
    context: any,
    customer: ICustomer,
    sites: ISite[],
  ): number | null {
    const landingSiteId = +(context?.landingPage?.site?.id || 0);
    if (!landingSiteId) {
      return null;
    }

    const landingCustomerId = +(context?.landingPage?.site?.customer?.id || 0);
    if (landingCustomerId && +landingCustomerId !== +customer?.id) {
      return null;
    }

    const siteInCustomer = (sites || []).some(
      (site) => +site.id === landingSiteId,
    );
    return siteInCustomer ? landingSiteId : null;
  }

  private getActiveCustomer(context: any): ICustomer | null {
    const customers = context?.customers || [];
    if (!customers.length) return null;

    const effectiveCustomerId = context?.session?.effective_customer_id;
    if (effectiveCustomerId) {
      const scopedCustomer = customers.find(
        (customer) => +customer.id === +effectiveCustomerId,
      );
      if (scopedCustomer) {
        return scopedCustomer as ICustomer;
      }
    }

    if (context?.activeCustomer?.id) {
      return context.activeCustomer as ICustomer;
    }

    return customers[0] || null;
  }

  private switchClinicAndContinue(sites_id: number) {
    this.authService.switchClinic(sites_id).subscribe({
      next: (context) => this.handleContext(context),
      error: () => {
        this.loading = false;
        this.router.navigate(['/context/select-clinic']);
      },
    });
  }

  private navigateToOperationalRoute(
    context: any,
    customer: ICustomer,
    site: ISite,
  ) {
    if (!customer?.identifier || !site?.identifier) {
      this.router.navigate(['/context/select-clinic']);
      return;
    }

    const moduleIdentifier = this.resolveModuleIdentifier(
      context,
      customer,
      site,
    );

    this.router.navigate([
      '/',
      customer.identifier,
      site.identifier,
      moduleIdentifier,
    ]);
  }

  private resolveModuleIdentifier(
    context: any,
    customer: ICustomer,
    site: ISite,
  ): string {
    const defaultModule = this.resolveFirstAllowedModule(customer, site);
    const allowedModules = new Set([
      'appointments',
      'dashboard',
      'patients',
      'sales',
      'statistics',
      'tasks',
    ]);

    const landing = context?.landingPage;
    const landingModule = this.normalizeModuleIdentifier(
      landing?.module?.identifier,
    );
    const landingSiteId = landing?.site?.id;

    if (!landingModule || !allowedModules.has(landingModule)) {
      return defaultModule;
    }

    if (!landingSiteId || +landingSiteId !== +site?.id) {
      return defaultModule;
    }

    if (
      this.hasModulePermission(customer, site, landingModule) &&
      this.hasCommercialModuleEnabled(customer, landingModule)
    ) {
      return landingModule;
    }

    return defaultModule;
  }

  private normalizeModuleIdentifier(
    identifier: string | null | undefined,
  ): string {
    const raw = `${identifier || ''}`.trim().toLowerCase();
    const aliases: Record<string, string> = {
      analytics: 'statistics',
      stats: 'statistics',
    };

    return aliases[raw] || raw;
  }

  private resolveFirstAllowedModule(customer: ICustomer, site: ISite): string {
    const preferredOrder = [
      'appointments',
      'dashboard',
      'patients',
      'sales',
      'statistics',
      'tasks',
    ];

    return (
      preferredOrder.find(
        (moduleIdentifier) =>
          this.hasModulePermission(customer, site, moduleIdentifier) &&
          this.hasCommercialModuleEnabled(customer, moduleIdentifier),
      ) || 'tasks'
    );
  }

  private hasCommercialModuleEnabled(
    customer: ICustomer,
    moduleIdentifier: string,
  ): boolean {
    const normalized = this.normalizeModuleIdentifier(moduleIdentifier);
    if (normalized === 'appointments' || normalized === 'patients') {
      return true;
    }
    const modules = customer?.commercialModules as any;
    if (!modules) return normalized !== 'incidents';
    if (normalized === 'statistics') {
      return modules.analytics !== false && modules.statistics !== false;
    }
    return modules[normalized] !== false;
  }

  private hasModulePermission(
    customer: ICustomer,
    site: ISite,
    moduleIdentifier: string,
  ): boolean {
    const requiredPermissionsByModule: Record<string, string[]> = {
      appointments: [
        'APPOINTMENTS.READ',
        'APPOINTMENTS.CREATE',
        'APPOINTMENTS.UPDATE',
        'APPOINTMENTS.DELETE',
      ],
      dashboard: ['DASHBOARD.READ', 'STATISTICS.READ'],
      incidents: ['INCIDENTS.READ'],
      patients: [
        'PATIENTS.READ',
        'PATIENTS.CREATE',
        'PATIENTS.UPDATE',
        'PATIENTS.DELETE',
      ],
      sales: ['SALES.READ', 'SALES.CREATE', 'SALES.UPDATE', 'SALES.DELETE'],
      statistics: ['STATISTICS.READ'],
      tasks: [],
    };

    const requiredPermissions =
      requiredPermissionsByModule[moduleIdentifier] || [];
    if (!requiredPermissions.length) {
      return true;
    }

    return requiredPermissions.some((permission) =>
      this.session.hasPermission(customer, site, permission),
    );
  }
}
